import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa6';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import ProgressiveImage from './ProgressiveImage';

const LEGACY_PRINT_PRODUCT_ID = process.env.REACT_APP_PRINT_PRODUCT_ID || process.env.PRINT_PRODUCT_ID || '';

const getSafeItem = (items, index) => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  const safeIndex = ((index % items.length) + items.length) % items.length;
  return { item: items[safeIndex], safeIndex };
};

function GalleryModal({ items = [], isOpen = false, initialIndex = 0, onClose, printCatalog = null }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isFullImageReady, setIsFullImageReady] = useState(false);
  const contentRef = useRef(null);
  const preloadCache = useRef(new Map());
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [initialIndex, isOpen]);

  const applyDelta = useCallback(
    (delta) => {
      setCurrentIndex((previousIndex) => {
        const entry = getSafeItem(items, previousIndex + delta);
        if (!entry || entry.safeIndex === undefined) {
          return previousIndex;
        }
        return entry.safeIndex;
      });
    },
    [items]
  );

  const handleNext = useCallback(() => applyDelta(1), [applyDelta]);
  const handlePrev = useCallback(() => applyDelta(-1), [applyDelta]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      } else if (event.key === 'ArrowRight') {
        applyDelta(1);
      } else if (event.key === 'ArrowLeft') {
        applyDelta(-1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [applyDelta, isOpen, onClose]);
  useEffect(() => {
    if (!isOpen) return;

    const cleanup = () => {
      preloadCache.current.clear();
    };

    return cleanup;
  }, [isOpen]);

  const prefetchImage = useCallback((src) => {
    if (!src || preloadCache.current.has(src)) {
      return;
    }

    const img = new Image();
    img.src = src;
    preloadCache.current.set(src, img);
  }, []);

  useEffect(() => {
    if (!isOpen || !items.length) return;

    const indicesToPrefetch = [currentIndex, currentIndex + 1, currentIndex - 1];

    indicesToPrefetch.forEach((targetIndex) => {
      const entry = getSafeItem(items, targetIndex);
      if (!entry) return;
      const src = entry.item?.image?.src || entry.item?.src;
      if (src) {
        prefetchImage(src);
      }
    });
  }, [currentIndex, isOpen, items, prefetchImage]);

  const currentEntry = useMemo(
    () => getSafeItem(items, currentIndex),
    [items, currentIndex]
  );
  const currentItem = currentEntry?.item;
  const imageSrc = currentItem?.image?.src || currentItem?.src || '';
  const previewSrc = currentItem?.image?.preview?.src || currentItem?.preview?.src || '';
  const rawTitle = currentItem?.title || currentItem?.name || 'Untitled';
  const title = rawTitle.trim();
  const description = currentItem?.description || '';
  const descriptionHasHtml = useMemo(() => {
    if (typeof description !== 'string') return false;
    const trimmed = description.trim();
    if (trimmed.length === 0) return false;
    return /<\/?[a-z][\s\S]*>/i.test(trimmed);
  }, [description]);

  const resolvePrintProductId = useCallback(
    (item) => {
      if (!item?.category || !printCatalog) {
        return null;
      }

      if (printCatalog instanceof Map) {
        const entry = printCatalog.get(item.category);
        if (!entry) return null;
        if (typeof entry === 'string') return entry;
        return entry.productId || entry.id || null;
      }

      if (typeof printCatalog === 'object') {
        const entry = printCatalog[item.category];
        if (!entry) return null;
        if (typeof entry === 'string') return entry;
        return entry.productId || entry.id || null;
      }

      return null;
    },
    [printCatalog]
  );

  const printProductId = useMemo(
    () => resolvePrintProductId(currentItem) || LEGACY_PRINT_PRODUCT_ID,
    [currentItem, resolvePrintProductId]
  );

  useEffect(() => {
    if (!isOpen) {
      setIsFullImageReady(false);
      return;
    }

    setIsFullImageReady(false);
  }, [imageSrc, isOpen]);

  if (!isOpen || !currentEntry) return null;

  const canShopPrint = Boolean(printProductId && currentItem?.used_for_prints);
  const printLink = canShopPrint
    ? `/product/${printProductId}?artwork=${encodeURIComponent(title)}`
    : null;

  const content = (
    <div className="gallery-modal-overlay" role="dialog" aria-modal="true">
      <div
        className="gallery-modal-content"
        ref={contentRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="gallery-modal-close"
          aria-label="Close gallery"
          onClick={() => onClose?.()}
        >
          ×
        </button>
        <div className="gallery-modal-image-container">
          <button
            type="button"
            className="gallery-modal-nav gallery-modal-nav--prev"
            aria-label="Previous image"
            onClick={handlePrev}
          >
            <FaAngleLeft />
          </button>

        
        {imageSrc || previewSrc ? (
          <>
            <ProgressiveImage
              key={`${imageSrc || previewSrc}-${currentEntry.safeIndex}`}
              src={imageSrc}
              placeholder={previewSrc}
              alt={title}
              className="gallery-modal-image"
              onFullLoad={() => setIsFullImageReady(true)}
              onError={() => setIsFullImageReady(true)}
            >
              {canShopPrint && (
                <Link
                  to={printLink}
                  className="gallery-modal-shop-link"
                  onClick={() => onClose?.()}
                >
                  Shop this print
                </Link>
              )}
            </ProgressiveImage>
            {!previewSrc && !isFullImageReady && (
              <div className="gallery-modal-loader" aria-hidden="true">
                Loading image…
              </div>
            )}
          </>
        ) : (
          <div className="gallery-modal-image--placeholder">No image available</div>
        )}
          <button
            type="button"
            className="gallery-modal-nav gallery-modal-nav--next"
            aria-label="Next image"
            onClick={handleNext}
          >
            <FaAngleRight />
          </button>
        </div>

        <div className="gallery-modal-meta">
          <h3 className="gallery-modal-title">{title}</h3>
          {description && (
            descriptionHasHtml ? (
              <p
                className="gallery-modal-description"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            ) : (
              <p className="gallery-modal-description">{description}</p>
            )
          )}
          <p className="gallery-modal-counter">
            {currentEntry.safeIndex + 1} / {items.length}
          </p>
        </div>

        
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export default GalleryModal;
